import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {zodResolver} from "@hookform/resolvers/zod";
import {downloadService, useContext} from "@mytour/platform";
import type {ICustomer, JardinierReport} from "@mytour/shared";
import {getCustomerFilenameSlug, getLabel} from "@mytour/shared";
import {FloatingField} from "@mytour/ui/form";
import axios from "axios";
import dayjs from "dayjs";
import {useState} from "react";
import {useForm} from "react-hook-form";
import {Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner} from "reactstrap";
import {z} from "zod";
import {useCustomer, useUpdateCustomer} from "../../../../client/query/customerQueries";
import {jardinierReportApiService} from "../query/jardinierReportApi.service";
import {useResendJardinierReport} from "../query/jardinierReportQueries";

const resendEmailSchema = z.object({email: z.string().email("Adresse email invalide")});
type ResendEmailForm = z.infer<typeof resendEmailSchema>;

type Props = {
  eventId: string;
  report: JardinierReport;
  customer: ICustomer;
  // `failed`/`bounced` : un envoi a été tenté et a échoué (adresse existante).
  isFailure: boolean;
  onClose: () => void;
};

/**
 * Popup "Récapitulatif et envoi" réouverte sur un compte rendu déjà enregistré — miroir de
 * `CertificatRamonageResendModal` (#1270) : réutilise le PDF déjà stocké, ne régénère rien, ne
 * modifie jamais le compte rendu lui-même. Le champ email éditable s'affiche dès qu'un envoi
 * est impossible en l'état : email absent (`!hasEmail`) ou envoi confirmé en échec (`isFailure`).
 */
export function JardinierReportResendModal({
  eventId,
  report,
  customer,
  isFailure,
  onClose,
}: Props) {
  const {context} = useContext();
  const {resendJardinierReport, isResendingJardinierReport} = useResendJardinierReport();
  const {updateCustomer, isUpdatingCustomer} = useUpdateCustomer();
  /**
   * Refetch systématique : `customer` en props peut être périmé (staleTime 5min sur les deux
   * call-sites) — corriger l'email doit écrire l'état frais de la fiche client, pas écraser
   * silencieusement une modification concurrente faite ailleurs pendant que la popup était ouverte.
   */
  const {customer: freshCustomer, isCustomerLoading} = useCustomer(customer.id, true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    control,
    handleSubmit,
    formState: {errors},
  } = useForm<ResendEmailForm>({
    resolver: zodResolver(resendEmailSchema),
    defaultValues: {email: customer.email ?? ""},
  });

  const isPending = isResendingJardinierReport || isUpdatingCustomer || isCustomerLoading;
  const hasEmail = !!customer.email;
  const needsEmailCorrection = isFailure || !hasEmail;
  const canEditCustomer =
    context?.role === "manager" ||
    (context?.role === "worker" && context.permissions.canEditCustomer);

  return (
    <Modal isOpen toggle={onClose}>
      <ModalHeader toggle={onClose}>
        Compte rendu jardinier
        <div style={{fontSize: "12px", color: "#6c757d", fontWeight: 400, marginTop: "2px"}}>
          Récapitulatif et envoi
        </div>
      </ModalHeader>
      <ModalBody>
        <Button
          color="primary"
          style={{width: "100%", marginBottom: "12px"}}
          onClick={handleDownload}
          disabled={isPending || isDownloading}
        >
          {isDownloading ? <Spinner size="sm" /> : <FontAwesomeIcon icon="file-pdf" />}
          &nbsp; Télécharger le compte rendu
        </Button>

        {needsEmailCorrection ? (
          <>
            <Alert color="danger">
              <strong>{hasEmail ? "Email non envoyé" : "Adresse email manquante"}</strong>
              <br />
              {hasEmail ? (
                "Le compte rendu n'a pas pu être envoyé à cette adresse."
              ) : (
                <>
                  {getLabel(customer)}
                  <b>{" n'a aucun email"}</b>
                  {" renseigné."}
                </>
              )}
            </Alert>
            {canEditCustomer ? (
              <FloatingField
                name="email"
                label="Adresse email"
                control={control}
                error={errors.email}
                disabled={isPending}
              />
            ) : hasEmail ? (
              <p style={{fontSize: "14px"}}>
                Adresse actuelle : {customer.email}. Seul un manager peut la corriger — vous pouvez
                renvoyer à cette adresse telle quelle.
              </p>
            ) : (
              <p style={{fontSize: "14px"}}>Seul un manager peut ajouter une adresse email.</p>
            )}
          </>
        ) : (
          <p style={{fontSize: "14px"}}>
            Un email avec le compte rendu sera envoyé à{" "}
            <a href={`mailto:${customer.email}`}>{customer.email}</a>.
          </p>
        )}

        {error && (
          <Alert color="danger" style={{marginTop: "12px"}}>
            {error}
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={isPending}>
          ← Retour
        </Button>
        {needsEmailCorrection ? (
          <Button
            color="primary"
            onClick={canEditCustomer ? handleSubmit(handleCorrectAndSend) : handleSend}
            disabled={isPending || (!canEditCustomer && !hasEmail)}
          >
            {isPending && <Spinner size="sm" />}{" "}
            {canEditCustomer ? "Corriger et envoyer" : "Renvoyer"}
          </Button>
        ) : (
          <Button color="primary" onClick={handleSend} disabled={isPending || !hasEmail}>
            {isPending && <Spinner size="sm" />} Envoyer par email
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );

  async function handleDownload() {
    try {
      setIsDownloading(true);
      const blob = await jardinierReportApiService.downloadFile(eventId);
      const parsedDate = dayjs(report.signatureTechnicienDate).format("YYYY-MM-DD");
      const fileName = `compte-rendu-jardinier_${getCustomerFilenameSlug(customer)}_${parsedDate}.pdf`;
      await downloadService.download(blob, fileName);
    } catch (downloadError) {
      setError(
        buildBlobMissingMessage(downloadError, "Erreur lors du téléchargement du compte rendu.")
      );
    } finally {
      setIsDownloading(false);
    }
  }

  async function handleSend() {
    try {
      setError(null);
      const result = await resendJardinierReport(eventId);
      applyResendResult(result);
    } catch (sendError) {
      setError(buildBlobMissingMessage(sendError, "Erreur lors de l'envoi. Veuillez réessayer."));
    }
  }

  async function handleCorrectAndSend(data: ResendEmailForm) {
    try {
      setError(null);
      if (!freshCustomer) {
        setError("Fiche client introuvable — impossible de corriger l'email.");
        return;
      }
      if (data.email !== freshCustomer.email) {
        await updateCustomer({...freshCustomer, email: data.email});
      }
      const result = await resendJardinierReport(eventId);
      applyResendResult(result);
    } catch (sendError) {
      setError(buildBlobMissingMessage(sendError, "Erreur lors de l'envoi. Veuillez réessayer."));
    }
  }

  /**
   * `resend()` résout toujours (jamais de rejet HTTP) même quand l'envoi échoue —
   * emailStatus doit être lu explicitement, sinon un échec se ferme comme un succès.
   */
  function applyResendResult(result: {emailStatus: string; emailMessage?: string}) {
    if (result.emailStatus === "sent") {
      onClose();
      return;
    }
    setError(result.emailMessage ?? "L'envoi n'a pas abouti. Vérifiez l'adresse et réessayez.");
  }
}

function buildBlobMissingMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error) && error.response?.status === 404) {
    return "Le compte rendu stocké est introuvable — contactez le support, il ne peut pas être renvoyé depuis cette popup.";
  }
  return fallback;
}
